// The scenario engine. A scenario says where an experiment is in its lifecycle (phase, day, outcome
// profile); `buildPatch` turns it into a wire-format patch that is shallow-merged over the real
// GET /experiments/:id response, so every surface renders the simulated state without the backend
// ever seeing it. Ported from the Ember prototype (customerio/ui#17200, simulator/scenario.ts).
import type {
  ExperimentConfig,
  ExperimentsAttachmentSummary,
  ExperimentsExperimentWithResults,
} from '@customerio/hydra-ui-api';
import { evaluate, type SimResults, type SimVariantConfig, type VariantCounts } from './stats';

export const DAY_SECONDS = 86_400;
const DAILY_EXPOSURES = 200;
const BASE_RATE = 0.437;
const DEFAULT_DURATION_DAYS = 14;
const DEFAULT_WINDOW_DAYS = 7;
const DEFAULT_CONFIDENCE = 0.9;
const RESULTS_AGE_SECONDS = 12 * 60;
const APPLIED_AFTER_SECONDS = 3600;

export const SIM_KINDS = ['actual', 'ab', 'multivariant'] as const;
export type SimKind = (typeof SIM_KINDS)[number];

export const SIM_PROFILE_IDS = ['b_ahead', 'b_strong', 'flat', 'control_best'] as const;
export type SimProfile = (typeof SIM_PROFILE_IDS)[number];

export type SimPhase = 'draft' | 'running' | 'paused' | 'observing' | 'ready_to_decide' | 'completed' | 'stopped';

export type Scenario = {
  version: 1;
  preset: string;
  kind: SimKind;
  profile: SimProfile;
  /** Days since the experiment started: the simulated "today". */
  day: number;
  /** A fixed start (unix seconds). Left out for a live simulation, which anchors day N to now. */
  startAt?: number;
  draft?: boolean;
  durationDays?: number;
  pausedAtDay?: number | null;
  stoppedAtDay?: number | null;
  completedAtDay?: number | null;
  winnerVariantId?: string | null;
  decidedEarly?: boolean;
  applied?: boolean;
};

/** What the engine reads from the real experiment. */
export type EngineSource = Pick<ExperimentsExperimentWithResults, 'id' | 'config' | 'attachments'>;

type PresetScenario = Omit<Scenario, 'version' | 'kind' | 'preset'>;

export type SimPreset = {
  id: string;
  label: string;
  scenario: (variants: SimVariantConfig[]) => PresetScenario;
};

function secondId(variants: SimVariantConfig[]): string | null {
  const second = variants.find((variant, index) => index > 0 && variant.role !== 'holdout');
  return second?.id ?? variants[0]?.id ?? null;
}

export const SIM_PRESETS: readonly SimPreset[] = [
  { id: 'draft', label: 'Draft', scenario: () => ({ profile: 'b_ahead', day: 0, draft: true }) },
  { id: 'running_early', label: 'Running, not enough data', scenario: () => ({ profile: 'b_ahead', day: 0.5 }) },
  { id: 'running_inconclusive', label: 'Running, leader too early', scenario: () => ({ profile: 'b_ahead', day: 10 }) },
  { id: 'running_winner', label: 'Running, already significant', scenario: () => ({ profile: 'b_strong', day: 10 }) },
  { id: 'paused', label: 'Paused', scenario: () => ({ profile: 'b_ahead', day: 10, pausedAtDay: 10 }) },
  { id: 'observing', label: 'Observing', scenario: () => ({ profile: 'b_ahead', day: 17 }) },
  { id: 'decision_winner', label: 'Decision needed, winner', scenario: () => ({ profile: 'b_strong', day: 23 }) },
  { id: 'decision_flat', label: 'Decision needed, no difference', scenario: () => ({ profile: 'flat', day: 23 }) },
  {
    id: 'decision_control',
    label: 'Decision needed, control best',
    scenario: () => ({ profile: 'control_best', day: 23 }),
  },
  {
    id: 'completed',
    label: 'Completed, not applied',
    scenario: (variants) => ({ profile: 'b_strong', day: 24, completedAtDay: 24, winnerVariantId: secondId(variants) }),
  },
  {
    id: 'completed_applied',
    label: 'Completed, winner applied',
    scenario: (variants) => ({
      profile: 'b_strong',
      day: 25,
      completedAtDay: 24,
      winnerVariantId: secondId(variants),
      applied: true,
    }),
  },
  { id: 'stopped', label: 'Stopped', scenario: () => ({ profile: 'b_ahead', day: 9, stoppedAtDay: 9 }) },
];

const PROFILE_MULTIPLIERS = {
  b_ahead: { treatments: [1.073, 0.944, 0.97], holdout: 0.0916 },
  b_strong: { treatments: [1.12, 0.95, 0.97], holdout: 0.09 },
  flat: { treatments: [1.004, 0.997, 1.002], holdout: 0.09 },
  control_best: { treatments: [0.86, 0.85, 0.88], holdout: 0.09 },
} satisfies Record<SimProfile, { treatments: number[]; holdout: number }>;

const SYNTHETIC = {
  ab: [
    { id: 'sim_a', name: 'Variant A', role: 'control', allocation: 50 },
    { id: 'sim_b', name: 'Variant B', role: 'treatment', allocation: 50 },
  ],
  multivariant: [
    { id: 'sim_a', name: 'Variant A', role: 'control', allocation: 30 },
    { id: 'sim_b', name: 'Variant B', role: 'treatment', allocation: 30 },
    { id: 'sim_c', name: 'Variant C', role: 'treatment', allocation: 30 },
    { id: 'sim_h', name: 'Holdout', role: 'holdout', allocation: 10 },
  ],
} satisfies Record<Exclude<SimKind, 'actual'>, SimVariantConfig[]>;

export function variantsFor(raw: EngineSource, kind: SimKind): SimVariantConfig[] {
  if (kind !== 'actual') {
    return SYNTHETIC[kind];
  }
  const actual = raw.config.variants.map(({ id, name, role, allocation }) => ({ id, name, role, allocation }));
  return actual.length >= 2 ? actual : SYNTHETIC.ab;
}

function presetById(presetId: string): SimPreset {
  return SIM_PRESETS.find((preset) => preset.id === presetId) ?? SIM_PRESETS[2];
}

export function scenarioFromPreset(raw: EngineSource, presetId: string, kind: SimKind): Scenario {
  const preset = presetById(presetId);
  return { version: 1, preset: preset.id, kind, ...preset.scenario(variantsFor(raw, kind)) };
}

function positiveOr(value: number | undefined, fallback: number): number {
  return value != null && value > 0 ? value : fallback;
}

export function timingOf(raw: EngineSource, scenario: Pick<Scenario, 'durationDays'>) {
  const duration = scenario.durationDays ?? positiveOr(raw.config.duration_days, DEFAULT_DURATION_DAYS);
  const window = positiveOr(raw.config.conversion_window_days, DEFAULT_WINDOW_DAYS);
  return { duration, window };
}

// Explicit end states win over the clock, in the same order as the prototype.
const FIXED_PHASES: ((scenario: Scenario) => SimPhase | null)[] = [
  (scenario) => (scenario.draft === true ? 'draft' : null),
  (scenario) => (scenario.stoppedAtDay != null ? 'stopped' : null),
  (scenario) => (scenario.completedAtDay != null ? 'completed' : null),
  (scenario) => (scenario.pausedAtDay != null ? 'paused' : null),
];

function clockPhase(raw: EngineSource, scenario: Scenario): SimPhase {
  const { duration, window } = timingOf(raw, scenario);
  if (scenario.day < duration) {
    return 'running';
  }
  return scenario.day < duration + window + 1 ? 'observing' : 'ready_to_decide';
}

export function phaseOf(raw: EngineSource, scenario: Scenario): SimPhase {
  for (const fixed of FIXED_PHASES) {
    const phase = fixed(scenario);
    if (phase != null) {
      return phase;
    }
  }
  return clockPhase(raw, scenario);
}

/** Days of enrollment that actually happened by "today". */
function enrolledDays(raw: EngineSource, scenario: Scenario): number {
  const { duration } = timingOf(raw, scenario);
  const stops = [scenario.day, duration, scenario.pausedAtDay, scenario.stoppedAtDay, scenario.completedAtDay];
  return Math.max(0, Math.min(...stops.filter((day) => typeof day === 'number')));
}

function variantRate(variant: SimVariantConfig, index: number, profile: SimProfile, treatmentIndex: number): number {
  const multipliers = PROFILE_MULTIPLIERS[profile];
  if (variant.role === 'holdout') {
    return BASE_RATE * multipliers.holdout;
  }
  return index === 0 ? BASE_RATE : BASE_RATE * (multipliers.treatments[treatmentIndex] ?? 1);
}

function countsFor(variants: SimVariantConfig[], profile: SimProfile, days: number): VariantCounts {
  const out: VariantCounts = {};
  let treatmentIndex = 0;
  variants.forEach((variant, index) => {
    const isTreatment = index > 0 && variant.role !== 'holdout';
    const rate = variantRate(variant, index, profile, treatmentIndex);
    treatmentIndex += Number(isTreatment);
    // A small per-variant offset keeps counts from looking machine-made.
    const offset = 1 + ((index * 7) % 5) / 100;
    const exposed = Math.round(((DAILY_EXPOSURES * variant.allocation) / 100) * days * offset);
    out[variant.id] = { exposed, converted: Math.round(exposed * rate) };
  });
  return out;
}

const ENROLLMENT_ENDED = new Set<SimPhase>(['observing', 'ready_to_decide', 'completed', 'stopped']);

export type SimulatedResults = SimResults & { experiment_id: number; computed_at: number };

export type SimMeta = {
  preset: string;
  kind: SimKind;
  profile: SimProfile;
  day: number;
  applied: boolean;
  /** The simulated "now" (unix seconds). */
  now: number;
};

/** The fields a simulation overrides on the raw experiment GET response (snake_case, as the API sends them). */
export type ExperimentPatch = {
  status: SimPhase;
  started_at: number | null;
  enrollment_ends_at: number | null;
  observation_ends_at: number | null;
  finalization_at: number | null;
  paused_at: number | null;
  stopped_at: number | null;
  completed_at: number | null;
  decided_early_at: number | null;
  winner_variant_id: string | null;
  exposed_total: number;
  exposures_by_variant: { variant_id: string; exposed: number }[];
  latest_results: SimulatedResults | null;
  sim: SimMeta;
  /** Only for the synthetic variant setups, which replace the real variants. */
  config?: ExperimentConfig;
  /** Only once a winner is applied: applying detaches the experiment from its campaigns. */
  attachments?: ExperimentsAttachmentSummary[];
};

type Clock = {
  start: number;
  simNow: number;
  at: (day: number | null | undefined) => number | null;
  enrollmentEnds: number;
  window: number;
};

function clockOf(raw: EngineSource, scenario: Scenario, nowSeconds: number): Clock {
  const start = scenario.startAt ?? nowSeconds - Math.round(scenario.day * DAY_SECONDS);
  const { duration, window } = timingOf(raw, scenario);
  // While paused the enrollment clock stops, so the end moves out by however long it has been paused.
  const pausedFor = scenario.pausedAtDay == null ? 0 : scenario.day - scenario.pausedAtDay;
  return {
    start,
    simNow: start + Math.round(scenario.day * DAY_SECONDS),
    at: (day) => (day == null ? null : start + Math.round(day * DAY_SECONDS)),
    enrollmentEnds: start + Math.round((duration + pausedFor) * DAY_SECONDS),
    window,
  };
}

function lifecycleDates(clock: Clock, isDraft: boolean) {
  if (isDraft) {
    return { started_at: null, enrollment_ends_at: null, observation_ends_at: null, finalization_at: null };
  }
  return {
    started_at: clock.start,
    enrollment_ends_at: clock.enrollmentEnds,
    observation_ends_at: clock.enrollmentEnds + clock.window * DAY_SECONDS,
    finalization_at: clock.enrollmentEnds + (clock.window + 1) * DAY_SECONDS,
  };
}

type ResultsInput = {
  raw: EngineSource;
  variants: SimVariantConfig[];
  counts: VariantCounts;
  phase: SimPhase;
  simNow: number;
};

function resultsFor({ raw, variants, counts, phase, simNow }: ResultsInput): SimulatedResults | null {
  if (phase === 'draft') {
    return null;
  }
  return {
    experiment_id: raw.id,
    computed_at: simNow - RESULTS_AGE_SECONDS,
    ...evaluate({
      variants,
      counts,
      confidenceLevel: positiveOr(raw.config.test.confidence_level, DEFAULT_CONFIDENCE),
      enrollmentEnded: ENROLLMENT_ENDED.has(phase),
    }),
  };
}

function appliedAttachments(raw: EngineSource, appliedAt: number): ExperimentsAttachmentSummary[] {
  return raw.attachments.map((attachment) =>
    attachment.detached_at == null ? { ...attachment, detached_at: appliedAt } : attachment,
  );
}

function optionalFields(raw: EngineSource, scenario: Scenario, clock: Clock, variants: SimVariantConfig[]) {
  const extra: Pick<ExperimentPatch, 'config' | 'attachments'> = {};
  if (scenario.kind !== 'actual') {
    extra.config = { ...raw.config, variants: variants.map((variant) => ({ ...variant, version: 1 })) };
  }
  const completedAt = clock.at(scenario.completedAtDay);
  if (scenario.applied === true && completedAt != null) {
    extra.attachments = appliedAttachments(raw, completedAt + APPLIED_AFTER_SECONDS);
  }
  return extra;
}

function metaOf(scenario: Scenario, simNow: number): SimMeta {
  const { preset, kind, profile, day } = scenario;
  return { preset, kind, profile, day, applied: scenario.applied === true, now: simNow };
}

export function buildPatch(raw: EngineSource, scenario: Scenario, nowMs = Date.now()): ExperimentPatch {
  const clock = clockOf(raw, scenario, Math.floor(nowMs / 1000));
  const variants = variantsFor(raw, scenario.kind);
  const phase = phaseOf(raw, scenario);
  const counts = countsFor(variants, scenario.profile, enrolledDays(raw, scenario));
  return {
    status: phase,
    ...lifecycleDates(clock, phase === 'draft'),
    paused_at: clock.at(scenario.pausedAtDay),
    stopped_at: clock.at(scenario.stoppedAtDay),
    completed_at: clock.at(scenario.completedAtDay),
    decided_early_at: scenario.decidedEarly === true ? clock.at(scenario.completedAtDay) : null,
    winner_variant_id: scenario.winnerVariantId ?? null,
    exposed_total: Object.values(counts).reduce((sum, count) => sum + count.exposed, 0),
    exposures_by_variant: variants.map((variant) => ({ variant_id: variant.id, exposed: counts[variant.id].exposed })),
    latest_results: resultsFor({ raw, variants, counts, phase, simNow: clock.simNow }),
    sim: metaOf(scenario, clock.simNow),
    ...optionalFields(raw, scenario, clock, variants),
  };
}

/** A lifecycle command, as the transitions endpoint takes it (or `apply_winner`). */
export type SimCommand = {
  command: string;
  variant_id?: string | null;
  early?: boolean | null;
  enrollment_ends_at?: number | null;
  summary?: string | null;
};

type CommandHandler = (raw: EngineSource, scenario: Scenario, body: SimCommand) => Scenario;

function extendEnrollment(raw: EngineSource, scenario: Scenario, body: SimCommand): Scenario {
  const { duration } = timingOf(raw, scenario);
  if (body.enrollment_ends_at == null) {
    return { ...scenario, durationDays: duration + 7 };
  }
  const start = scenario.startAt ?? Date.now() / 1000 - scenario.day * DAY_SECONDS;
  return { ...scenario, durationDays: Math.max(duration, (body.enrollment_ends_at - start) / DAY_SECONDS) };
}

function resume(raw: EngineSource, scenario: Scenario): Scenario {
  const pausedFor = scenario.pausedAtDay == null ? 0 : scenario.day - scenario.pausedAtDay;
  const { duration } = timingOf(raw, scenario);
  return { ...scenario, pausedAtDay: null, durationDays: duration + pausedFor };
}

function chooseWinner(raw: EngineSource, scenario: Scenario, body: SimCommand): Scenario {
  const decidedEarly = body.early === true || phaseOf(raw, scenario) !== 'ready_to_decide';
  return {
    ...scenario,
    completedAtDay: scenario.day,
    pausedAtDay: null,
    winnerVariantId: body.variant_id ?? secondId(variantsFor(raw, scenario.kind)),
    decidedEarly,
  };
}

// What each lifecycle command does to a simulated experiment. Mirrors lifecycle.go's transitions.
const COMMANDS = new Map<string, CommandHandler>([
  ['start', (_raw, scenario) => ({ ...scenario, draft: false, day: 0.05 })],
  ['pause', (_raw, scenario) => ({ ...scenario, pausedAtDay: scenario.day })],
  ['resume', resume],
  ['stop', (_raw, scenario) => ({ ...scenario, stoppedAtDay: scenario.day, pausedAtDay: null })],
  ['end_enrollment', (_raw, scenario) => ({ ...scenario, durationDays: scenario.day })],
  ['extend_enrollment', extendEnrollment],
  ['choose_winner', chooseWinner],
  ['apply_winner', (_raw, scenario) => ({ ...scenario, applied: true })],
]);

export function simulateCommand(raw: EngineSource, scenario: Scenario, body: SimCommand): Scenario {
  const handler = COMMANDS.get(body.command);
  return handler == null ? { ...scenario } : handler(raw, scenario, body);
}
